export const ANSWER_MODES = {
  FREE: "free",
  CHOICES: "choices",
  PAIRS: "pairs",
  TEXT: "text",
  NUMBER_EXACT: "number_exact",
  NUMBER_RANGE: "number_range",
  DATE_EXACT: "date_exact",
  DATE_RANGE: "date_range",
  MANUAL: "manual",
} as const;

export type AnswerMode = (typeof ANSWER_MODES)[keyof typeof ANSWER_MODES];

export const ANSWER_MODE_LABELS: Record<string, string> = {
  free: "Free — any answer",
  choices: "Correct option(s)",
  pairs: "All pairs",
  text: "Text match",
  number_exact: "Is exactly",
  number_range: "Between (range)",
  date_exact: "Is exactly",
  date_range: "Date range",
  manual: "Manual — grader awards points",
};

export type QuestionTypeOption = {
  id: string;
  label: string;
  description: string;
  group: "choice" | "written" | "other";
};

export const QUESTION_TYPE_OPTIONS: QuestionTypeOption[] = [
  { id: "single_choice", label: "Single choice", description: "Pick one — radio, dropdown, Yes/No, or True/False.", group: "choice" },
  { id: "multiple_choice", label: "Multiple choice", description: "Checkboxes; mark every correct option.", group: "choice" },
  { id: "matching", label: "Matching", description: "Pair each left item with its own match. One-to-one — two lefts cannot share an answer.", group: "choice" },
  { id: "short_answer", label: "Short answer", description: "Single-line text response.", group: "written" },
  { id: "long_answer", label: "Long answer", description: "Multi-line text response.", group: "written" },
  { id: "fill_blank", label: "Fill in the blank", description: "A sentence with one or more blanks the learner completes.", group: "written" },
  { id: "number", label: "Number", description: "Numeric input.", group: "written" },
  { id: "date", label: "Date", description: "Calendar date (YYYY-MM-DD).", group: "written" },
  { id: "upload", label: "File upload", description: "Attach one or more files.", group: "other" },
  { id: "directions", label: "Directions", description: "Instructions only — not scored.", group: "other" },
];

export const QUESTION_TYPE_LABELS = Object.fromEntries(
  QUESTION_TYPE_OPTIONS.map((t) => [t.id, t.label]),
) as Record<string, string>;

export const QUESTION_TYPE_GROUPS: { key: string; label: string }[] = [
  { key: "choice", label: "Choice" },
  { key: "written", label: "Written" },
  { key: "other", label: "Other" },
];

export const SINGLE_CHOICE_STYLES = [
  { id: "radio", label: "Radio" },
  { id: "dropdown", label: "Dropdown" },
  { id: "yes_no", label: "Yes / No" },
  { id: "true_false", label: "True / False" },
] as const;

export type SingleChoiceStyle = (typeof SINGLE_CHOICE_STYLES)[number]["id"];

export type Scoring = {
  answerMode: AnswerMode;
  correctOptions: number[];
  textRule: { match: "includes" | "equals"; value: string; caseSensitive: boolean; values?: string[] };
  numberRule: { exact: string; min: string; max: string };
  dateRule: { exact: string; min: string; max: string };
};

export type ChoiceOption = { id: number; label: string };
export type MatchingPair = { id: number; left: string; right: string };
export type QuestionOption = ChoiceOption | MatchingPair;

export type CorrectKey = {
  optionId?: number | null;
  optionIds?: number[];
  answer?: string | null;
  blanks?: string[];
  index?: number | null;
  indexes?: number[];
};

const CHOICE = new Set(["single_choice", "multiple_choice", "true_false", "yes_no"]);
const TEXT = new Set(["short_answer", "long_answer", "fill_blank"]);

export function optionId(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(String(value).trim());
  return Number.isInteger(n) && n >= 1 ? n : null;
}

export function nextOptionId(options: { id?: unknown }[] | null | undefined): number {
  let max = 0;
  for (const row of options || []) {
    const id = optionId(row?.id);
    if (id != null && id > max) max = id;
  }
  return max + 1;
}

function takeUniqueId(requested: unknown, used: Set<number>, fallback: number): number {
  let id = optionId(requested);
  if (id == null || used.has(id)) id = fallback;
  while (used.has(id)) id += 1;
  used.add(id);
  return id;
}

function isMatchingRaw(raw: unknown): raw is { left?: unknown; right?: unknown; id?: unknown } {
  return Boolean(
    raw &&
      typeof raw === "object" &&
      !Array.isArray(raw) &&
      (raw as { label?: unknown }).label == null &&
      ("left" in raw || "right" in raw),
  );
}

export function normalizeChoiceOptions(list: unknown): ChoiceOption[] {
  const out: ChoiceOption[] = [];
  const used = new Set<number>();
  let next = 1;
  for (const raw of Array.isArray(list) ? list : []) {
    if (isMatchingRaw(raw)) continue;
    let label = "";
    let requested: unknown = null;
    if (typeof raw === "string" || typeof raw === "number") {
      label = String(raw);
    } else if (raw && typeof raw === "object") {
      requested = (raw as { id?: unknown }).id;
      const row = raw as { label?: unknown; text?: unknown; value?: unknown };
      label = String(row.label ?? row.text ?? row.value ?? "");
    }
    const id = takeUniqueId(requested, used, next);
    if (id >= next) next = id + 1;
    out.push({ id, label });
  }
  return out;
}

/** Compare matching labels: trim, collapse spaces, ignore case. IDs are not visible. */
export function matchingTextKey(value: unknown) {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function duplicateMatchingKeys(values: string[]) {
  const counts = new Map<string, number>();
  for (const key of values) {
    if (!key) continue;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return new Set([...counts].filter(([, n]) => n > 1).map(([key]) => key));
}

export function normalizeMatchingOptions(list: unknown): MatchingPair[] {
  const out: MatchingPair[] = [];
  const used = new Set<number>();
  let next = 1;
  for (const raw of Array.isArray(list) ? list : []) {
    if (!raw || typeof raw !== "object") continue;
    const row = raw as { id?: unknown; left?: unknown; right?: unknown };
    const id = takeUniqueId(row.id, used, next);
    if (id >= next) next = id + 1;
    out.push({ id, left: String(row.left ?? ""), right: String(row.right ?? "") });
  }
  return out;
}

function binaryOptionsFor(type?: string | null, presentation?: string | null): ChoiceOption[] | null {
  const t = String(type || "");
  const p = String(presentation || "");
  if (t === "true_false" || p === "true_false") {
    return [
      { id: 1, label: "True" },
      { id: 2, label: "False" },
    ];
  }
  if (t === "yes_no" || p === "yes_no") {
    return [
      { id: 1, label: "Yes" },
      { id: 2, label: "No" },
    ];
  }
  return null;
}

export function normalizeQuestionOptions(
  list: unknown,
  type?: string | null,
  presentation?: string | null,
): QuestionOption[] {
  const t = String(type || "");
  if (t === "matching") return normalizeMatchingOptions(list);
  if (
    t === "directions" ||
    t === "upload" ||
    t === "short_answer" ||
    t === "long_answer" ||
    t === "fill_blank" ||
    t === "number" ||
    t === "date"
  ) {
    return [];
  }
  const raw = Array.isArray(list) ? list : [];
  if (!raw.length) return binaryOptionsFor(t, presentation) || [];
  return normalizeChoiceOptions(raw);
}

function optionIdsFromKey(key: CorrectKey | null | undefined, options: QuestionOption[]): number[] {
  if (!key || typeof key !== "object") return [];
  const allowed = new Set(options.map((row) => row.id));
  if (Array.isArray(key.optionIds)) {
    return key.optionIds.map(optionId).filter((id): id is number => id != null && allowed.has(id));
  }
  if (key.optionId != null) {
    const id = optionId(key.optionId);
    return id != null && allowed.has(id) ? [id] : [];
  }
  if (Array.isArray(key.indexes) || key.index != null) {
    const idxs = Array.isArray(key.indexes) ? key.indexes : [key.index];
    return idxs.map((i) => options[Number(i)]?.id).filter((id): id is number => id != null);
  }
  const answer = String(key.answer ?? "").trim().toLowerCase();
  if (answer) {
    const hit = options.find((row) => "label" in row && String(row.label || "").trim().toLowerCase() === answer);
    return hit ? [hit.id] : [];
  }
  return [];
}

export function expectedOptionIds(
  question: { type?: string; options?: unknown; correct?: CorrectKey | null; presentation?: string | null } | null | undefined,
  scoring?: (Partial<Scoring> & { presentation?: string }) | null,
): number[] {
  const type = String(question?.type || "");
  const presentation = question?.presentation || scoring?.presentation || "";
  const options = normalizeQuestionOptions(question?.options, type, presentation);
  const fromKey = optionIdsFromKey(question?.correct, options);
  if (fromKey.length) return fromKey;
  const raw = scoring?.correctOptions;
  if (!Array.isArray(raw) || !raw.length) return [];
  const allowed = new Set(options.map((row) => row.id));
  const asIds = raw.map(optionId).filter((id): id is number => id != null && allowed.has(id));
  if (asIds.length) return asIds;
  return raw
    .map((label) =>
      options.find(
        (row) =>
          "label" in row &&
          (String(row.label) === String(label) || String(row.label).toLowerCase() === String(label).toLowerCase()),
      ),
    )
    .filter((row): row is QuestionOption => Boolean(row))
    .map((row) => row.id);
}

export function isMultiCorrect(correct: CorrectKey | null | undefined): boolean {
  return Boolean(correct && (Array.isArray(correct.optionIds) || Array.isArray(correct.indexes)));
}

export function normalizeCorrectKey(
  correct: CorrectKey | null | undefined,
  options: QuestionOption[],
  type?: string | null,
): CorrectKey {
  const t = String(type || "");
  const key = correct && typeof correct === "object" ? correct : {};
  if (t === "short_answer" || t === "long_answer" || t === "number" || t === "date") {
    return key.answer != null && String(key.answer) !== "" ? { answer: String(key.answer) } : {};
  }
  if (t === "fill_blank") {
    const blanks = Array.isArray(key.blanks) ? key.blanks.map((v) => String(v ?? "")) : [];
    const answer =
      key.answer != null && String(key.answer) !== "" ? String(key.answer) : String(blanks[0] || "");
    const out: CorrectKey = {};
    if (answer) out.answer = answer;
    if (blanks.length) out.blanks = blanks;
    return out;
  }
  if (t === "matching" || t === "upload" || t === "directions") return {};
  const ids = optionIdsFromKey(key, options);
  if (t === "multiple_choice") return ids.length ? { optionIds: ids } : {};
  if (isMultiCorrect(key)) return { optionIds: ids };
  if (ids.length === 1) return { optionId: ids[0] };
  if (ids.length > 1) return { optionIds: ids };
  return {};
}

export function modesForQuestionType(type: string): AnswerMode[] {
  const t = String(type || "");
  if (t === "directions") return [];
  if (t === "upload") return [ANSWER_MODES.FREE, ANSWER_MODES.MANUAL];
  if (t === "matching") return [ANSWER_MODES.FREE, ANSWER_MODES.PAIRS, ANSWER_MODES.MANUAL];
  if (CHOICE.has(t)) return [ANSWER_MODES.FREE, ANSWER_MODES.CHOICES, ANSWER_MODES.MANUAL];
  if (TEXT.has(t)) return [ANSWER_MODES.FREE, ANSWER_MODES.TEXT, ANSWER_MODES.MANUAL];
  if (t === "number") {
    return [ANSWER_MODES.FREE, ANSWER_MODES.NUMBER_EXACT, ANSWER_MODES.NUMBER_RANGE, ANSWER_MODES.MANUAL];
  }
  if (t === "date") {
    return [ANSWER_MODES.FREE, ANSWER_MODES.DATE_EXACT, ANSWER_MODES.DATE_RANGE, ANSWER_MODES.MANUAL];
  }
  return [ANSWER_MODES.FREE, ANSWER_MODES.MANUAL];
}

export function defaultScoringForType(type: string): Scoring {
  const t = String(type || "");
  const base: Omit<Scoring, "answerMode"> = {
    correctOptions: [],
    textRule: { match: "includes", value: "", caseSensitive: false },
    numberRule: { exact: "", min: "", max: "" },
    dateRule: { exact: "", min: "", max: "" },
  };
  if (CHOICE.has(t)) return { ...base, answerMode: ANSWER_MODES.CHOICES };
  if (t === "fill_blank") return { ...base, answerMode: ANSWER_MODES.TEXT };
  if (TEXT.has(t)) return { ...base, answerMode: ANSWER_MODES.MANUAL };
  if (t === "number") return { ...base, answerMode: ANSWER_MODES.NUMBER_EXACT };
  if (t === "date") return { ...base, answerMode: ANSWER_MODES.DATE_EXACT };
  if (t === "matching") return { ...base, answerMode: ANSWER_MODES.PAIRS };
  if (t === "upload") return { ...base, answerMode: ANSWER_MODES.MANUAL };
  return { ...base, answerMode: ANSWER_MODES.FREE };
}

export function normalizeScoring(scoring: Partial<Scoring> | null | undefined, type: string): Scoring {
  const base = defaultScoringForType(type);
  const stored = scoring && typeof scoring === "object" ? scoring : {};
  const allowed = modesForQuestionType(type);
  const answerMode = stored.answerMode && allowed.includes(stored.answerMode)
    ? stored.answerMode
    : base.answerMode;
  return {
    ...base,
    ...stored,
    answerMode,
    correctOptions: Array.isArray(stored.correctOptions)
      ? stored.correctOptions.map(optionId).filter((id): id is number => id != null)
      : [],
    textRule: {
      match: stored.textRule?.match === "equals" ? "equals" : "includes",
      value: String(stored.textRule?.value ?? ""),
      caseSensitive: stored.textRule?.caseSensitive === true,
      ...(Array.isArray(stored.textRule?.values)
        ? { values: stored.textRule.values.map((v) => String(v ?? "")) }
        : {}),
    },
    numberRule: {
      exact: stored.numberRule?.exact ?? "",
      min: stored.numberRule?.min ?? "",
      max: stored.numberRule?.max ?? "",
    },
    dateRule: {
      exact: stored.dateRule?.exact ?? "",
      min: stored.dateRule?.min ?? "",
      max: stored.dateRule?.max ?? "",
    },
  };
}

export function showAttemptControls(type: string, scoring: Partial<Scoring> | null | undefined) {
  if (type === "directions") return false;
  const mode = normalizeScoring(scoring, type).answerMode;
  return mode !== ANSWER_MODES.FREE && mode !== ANSWER_MODES.MANUAL;
}

/** True when attempt credit is on and attempt points are not strictly below question points. */
export function attemptPointsInvalid(
  points: unknown,
  awardAttemptPoints: unknown,
  attemptPoints: unknown,
) {
  if (awardAttemptPoints !== true) return false;
  const max = Math.max(0, Number(points) || 0);
  const att = Math.max(0, Number(attemptPoints) || 0);
  return att >= max;
}

export function scoringBadge(type: string, scoring: Partial<Scoring> | null | undefined) {
  if (type === "directions") return null;
  const mode = normalizeScoring(scoring, type).answerMode;
  if (mode === ANSWER_MODES.MANUAL) return { label: "Manual", kind: "todo" as const };
  if (mode === ANSWER_MODES.FREE) return { label: "Free", kind: "done" as const };
  return { label: "Auto", kind: "done" as const };
}

export const FILL_BLANK_TOKEN = "${blank}";
export const FILL_BLANK_SPAN = `<span class="al-cb-blank" contenteditable="false">${FILL_BLANK_TOKEN}</span>`;

const BLANK_SPAN_RE = /<span[^>]*class="[^"]*\bal-cb-blank\b[^"]*"[^>]*>[\s\S]*?<\/span>/gi;

export function countFillBlanks(value: string) {
  return (undecorateFillBlanks(String(value || "")).match(/\$\{blank\}/g) || []).length;
}

/** Expected fill-in answers, in blank order. One-string keys stay a one-item list. */
export function blankValuesFrom(
  textRule?: { value?: string; values?: string[] } | null,
  correct?: { answer?: string | null; blanks?: string[] } | null,
) {
  const rule = textRule && typeof textRule === "object" ? textRule : {};
  const key = correct && typeof correct === "object" ? correct : {};
  if (Array.isArray(rule.values) && rule.values.length) {
    return rule.values.map((v) => String(v ?? ""));
  }
  if (Array.isArray(key.blanks) && key.blanks.length) {
    return key.blanks.map((v) => String(v ?? ""));
  }
  return [String(rule.value ?? key.answer ?? "")];
}

export function padBlankValues(values: string[] | null | undefined, count: number) {
  const n = Math.max(1, Number(count) || 1);
  const src = Array.isArray(values) ? values : [];
  return Array.from({ length: n }, (_, i) => String(src[i] ?? ""));
}

export function decorateFillBlanks(value: string) {
  return undecorateFillBlanks(String(value || "")).replace(/\$\{blank\}/g, FILL_BLANK_SPAN);
}

export function undecorateFillBlanks(value: string) {
  return String(value || "").replace(BLANK_SPAN_RE, FILL_BLANK_TOKEN);
}

export function looksLikeHtml(value: string) {
  return /<\/?[a-z][\s\S]*>/i.test(String(value || ""));
}

export function promptToPlain(value: string) {
  const raw = undecorateFillBlanks(String(value || ""));
  if (!raw.trim()) return "";
  if (!looksLikeHtml(raw)) return raw;
  return raw
    .replace(/<\/(p|div|h[1-6]|li|br)\s*>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function promptToHtml(value: string) {
  const raw = undecorateFillBlanks(String(value || ""));
  if (!raw.trim()) return "";
  if (looksLikeHtml(raw)) return decorateFillBlanks(raw);
  return `<p>${raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>")}</p>`;
}

export function promptPreview(value: string) {
  const plain = promptToPlain(value).replace(/\$\{blank\}/g, "____").replace(/\s+/g, " ").trim();
  return plain || "(No prompt)";
}

/** One-file questions use 1. Multiple is uncapped — S3 holds the objects. */
export function allowMultipleUploads(scoring: unknown, type?: string | null) {
  if (String(type || "") !== "upload") return false;
  const raw = scoring && typeof scoring === "object" ? (scoring as { allowMultiple?: boolean; maxFiles?: unknown }) : {};
  if (raw.allowMultiple === false || Number(raw.maxFiles) === 1) return false;
  return true;
}

export function uploadFilesMax(scoring: unknown, type?: string | null) {
  if (String(type || "") !== "upload") return 0;
  return allowMultipleUploads(scoring, type) ? null : 1;
}

/** S3 prefix for learner answer files. Not the staff media catalog. */
export const SUBMISSION_UPLOAD_PREFIX = "learn/submissions";

export type UploadFileRef = {
  id: string;
  name: string;
  url: string;
  s3Key: string;
  size: number | null;
  mime: string;
};

export function uploadKeyPrefix(submissionId?: unknown, questionId?: unknown) {
  const sub = String(submissionId || "").trim();
  const q = String(questionId || "").trim();
  if (!sub || !q) return "";
  return `${SUBMISSION_UPLOAD_PREFIX}/${sub}/${q}/`;
}

function asUploadFile(raw: unknown): UploadFileRef | null {
  if (typeof raw === "string") {
    const url = raw.trim();
    return url ? { id: url, name: "", url, s3Key: "", size: null, mime: "" } : null;
  }
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const url = String(row.url || row.href || "").trim();
  const s3Key = String(row.s3Key || row.s3_key || "")
    .replace(/^\/+/, "")
    .trim();
  if (!url && !s3Key) return null;
  const sizeRaw = row.size ?? row.sizeBytes ?? row.size_bytes;
  const size =
    sizeRaw == null || sizeRaw === ""
      ? null
      : Number.isFinite(Number(sizeRaw))
        ? Math.max(0, Math.round(Number(sizeRaw)))
        : null;
  return {
    id: String(row.id || s3Key || url).trim(),
    name: String(row.name || row.originalFilename || row.original_filename || "")
      .trim()
      .slice(0, 500),
    url: url || "",
    s3Key,
    size,
    mime: String(row.mime || row.mimeType || row.mime_type || "")
      .trim()
      .slice(0, 200),
  };
}

export function collectUploadFiles(raw: unknown): UploadFileRef[] {
  if (raw == null) return [];
  if (typeof raw === "string") {
    const row = asUploadFile(raw);
    return row ? [row] : [];
  }
  if (typeof raw !== "object") return [];
  const out: UploadFileRef[] = [];
  const seen = new Set<string>();
  const push = (value: unknown) => {
    const row = asUploadFile(value);
    if (!row) return;
    const key = row.s3Key || row.url;
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(row);
  };
  if (Array.isArray(raw)) {
    raw.forEach(push);
    return out;
  }
  const record = raw as { files?: unknown; urls?: unknown; url?: unknown; answer?: unknown };
  if (Array.isArray(record.files)) record.files.forEach(push);
  if (!out.length && Array.isArray(record.urls)) record.urls.forEach(push);
  if (!out.length) push(record.url || record.answer);
  return out;
}

export function collectUploadUrls(raw: unknown): string[] {
  return collectUploadFiles(raw)
    .map((row) => row.url)
    .filter(Boolean);
}

export function normalizeUploadResponse(
  raw: unknown,
  scoring: unknown,
  type?: string | null,
  extras?: { submissionId?: unknown; questionId?: unknown },
) {
  const cap = uploadFilesMax(scoring, type);
  const prefix = uploadKeyPrefix(extras?.submissionId, extras?.questionId);
  let files = collectUploadFiles(raw).filter((row) => !prefix || !row.s3Key || row.s3Key.startsWith(prefix));
  if (cap) files = files.slice(0, cap);
  const urls = files.map((row) => row.url).filter(Boolean);
  if (!files.length) return { files: [] as UploadFileRef[], urls: [] as string[] };
  return { files, urls, url: urls[0] || "" };
}

function isIsoDate(value: unknown) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || "").trim());
}

function hasPrompt(question: { prompt?: unknown } | null | undefined) {
  return Boolean(promptToPlain(String(question?.prompt ?? "")).replace(/\$\{blank\}/g, "").trim());
}

export type QuestionCompletenessInput = {
  type?: string;
  prompt?: unknown;
  options?: unknown;
  correct?: CorrectKey | null;
  scoring?: Partial<Scoring> | null;
  presentation?: string | null;
  points?: number;
  awardAttemptPoints?: boolean;
  attemptPoints?: number;
};

/** Why a question still shows Incomplete. Empty = ready. */
export function questionIncompleteReasons(question: QuestionCompletenessInput | null | undefined): string[] {
  const type = String(question?.type || "");
  const scoring = normalizeScoring(question?.scoring, type);
  const stored = question?.scoring && typeof question.scoring === "object" ? question.scoring : {};
  const presentation = question?.presentation || (stored as { presentation?: string }).presentation || "";
  const options = normalizeQuestionOptions(question?.options, type, presentation);
  const reasons: string[] = [];

  if (type === "directions") {
    if (!hasPrompt(question)) reasons.push("Add directions.");
    return reasons;
  }

  if (type === "fill_blank") {
    if (!hasPrompt(question)) reasons.push("Add a prompt.");
    if (countFillBlanks(String(question?.prompt || "")) < 1) reasons.push("Insert at least one blank.");
  } else if (!hasPrompt(question)) {
    reasons.push("Add a prompt.");
  }

  if (CHOICE.has(type)) {
    const labeled = options.filter((row) => "label" in row && String(row.label || "").trim()).length;
    const binary =
      presentation === "yes_no" ||
      presentation === "true_false" ||
      type === "true_false" ||
      type === "yes_no";
    if (!binary && labeled < 2) reasons.push("Add at least two options.");
    if (scoring.answerMode === ANSWER_MODES.CHOICES && !expectedOptionIds(question, scoring).length) {
      reasons.push(type === "multiple_choice" ? "Mark the correct option(s)." : "Mark the correct option.");
    }
  }

  if (type === "matching") {
    const pairs = options.filter((row): row is MatchingPair => "left" in row);
    const filled = pairs.filter((row) => String(row.left || "").trim() && String(row.right || "").trim());
    const unfinished = pairs.filter((row) => {
      const left = String(row.left || "").trim();
      const right = String(row.right || "").trim();
      return (left && !right) || (!left && right);
    });
    if (filled.length < 2) reasons.push("Add at least two complete pairs.");
    else if (unfinished.length) reasons.push("Finish every pair (left and right).");
    else {
      if (duplicateMatchingKeys(filled.map((row) => matchingTextKey(row.left))).size) {
        reasons.push("Each left item must be unique.");
      }
      if (duplicateMatchingKeys(filled.map((row) => matchingTextKey(row.right))).size) {
        reasons.push("Each match must be unique.");
      }
    }
  }

  if (scoring.answerMode === ANSWER_MODES.TEXT) {
    if (type === "fill_blank") {
      const n = Math.max(1, countFillBlanks(String(question?.prompt || "")));
      const values = padBlankValues(blankValuesFrom(scoring.textRule, question?.correct), n);
      if (values.some((v) => !String(v || "").trim())) {
        reasons.push(n > 1 ? "Enter an answer for every blank." : "Enter the text to match.");
      }
    } else if (!String(scoring.textRule?.value || "").trim()) {
      reasons.push("Enter the text to match.");
    }
  }
  if (scoring.answerMode === ANSWER_MODES.NUMBER_EXACT) {
    const exact = String(scoring.numberRule?.exact ?? "").trim();
    if (!exact || !Number.isFinite(Number(exact))) reasons.push("Enter the exact number.");
  }
  if (scoring.answerMode === ANSWER_MODES.NUMBER_RANGE) {
    const min = String(scoring.numberRule?.min ?? "").trim();
    const max = String(scoring.numberRule?.max ?? "").trim();
    if (!min || !max || !Number.isFinite(Number(min)) || !Number.isFinite(Number(max))) {
      reasons.push("Enter a number range.");
    }
  }
  if (scoring.answerMode === ANSWER_MODES.DATE_EXACT && !isIsoDate(scoring.dateRule?.exact)) {
    reasons.push("Enter the exact date.");
  }
  if (scoring.answerMode === ANSWER_MODES.DATE_RANGE) {
    if (!isIsoDate(scoring.dateRule?.min) || !isIsoDate(scoring.dateRule?.max)) {
      reasons.push("Enter a date range.");
    }
  }

  if (attemptPointsInvalid(question?.points, question?.awardAttemptPoints === true, question?.attemptPoints)) {
    reasons.push("Attempt points must be less than the question points.");
  }

  return reasons;
}

export function questionIsComplete(question: QuestionCompletenessInput | null | undefined) {
  return questionIncompleteReasons(question).length === 0;
}

export function questionsAreComplete(questions: QuestionCompletenessInput[] | null | undefined) {
  return Array.isArray(questions) && questions.length > 0 && questions.every(questionIsComplete);
}

/**
 * Whether the learner has actually answered this question. Directions need
 * nothing. Matching needs every pair. Fill-in needs every blank. Author
 * completeness (`questionIsComplete`) is a different check.
 */
/** A choice or match with nothing to select is not a question a student can answer. */
export function shownToLearner(question: {
  type?: string;
  prompt?: string;
  options?: unknown;
  presentation?: string | null;
  matching?: { lefts?: Array<{ text?: string }> };
} | null | undefined) {
  const type = String(question?.type || "");
  if (type === "directions") {
    return Boolean(promptToPlain(String(question?.prompt || "")).replace(/\$\{blank\}/g, "").trim());
  }
  if (CHOICE.has(type)) {
    const options = normalizeQuestionOptions(question?.options, type, question?.presentation);
    return options.some((row) => "label" in row && String(row.label || "").trim());
  }
  if (type === "matching") {
    const pairs = normalizeQuestionOptions(question?.options, "matching");
    if (
      pairs.some(
        (row) => "left" in row && String(row.left || "").trim() && String(row.right || "").trim(),
      )
    ) {
      return true;
    }
    // The student payload sends the two columns separately and leaves options empty.
    const lefts = question?.matching?.lefts;
    return Array.isArray(lefts) && lefts.some((row) => String(row?.text || "").trim());
  }
  return true;
}

export function responseIsComplete(question: { type?: string; prompt?: string; options?: unknown; presentation?: string | null } | null | undefined, raw: unknown) {
  const type = String(question?.type || "");
  if (type === "directions") return true;
  if (CHOICE.has(type) && !shownToLearner(question)) return true;
  const src = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  if (type === "multiple_choice") {
    const ids = Array.isArray(src.optionIds)
      ? (src.optionIds as unknown[]).map(optionId).filter((id): id is number => id != null)
      : [];
    if (ids.length) return true;
    return optionId(src.optionId) != null;
  }
  if (CHOICE.has(type) || type === "true_false" || type === "yes_no") {
    return optionId(src.optionId) != null;
  }
  if (type === "matching") {
    const matching = (question as { matching?: { lefts?: Array<{ id?: unknown }> } } | null | undefined)?.matching;
    const lefts = Array.isArray(matching?.lefts) ? matching.lefts : [];
    const given = src.pairs && typeof src.pairs === "object" && !Array.isArray(src.pairs)
      ? (src.pairs as Record<string, unknown>)
      : {};
    if (lefts.length) {
      return lefts.every((row) => optionId(given[String(row.id ?? "")] ?? given[Number(row.id)]) != null);
    }
    const pairs = normalizeQuestionOptions(question?.options, "matching").filter(
      (row): row is MatchingPair => "left" in row && "right" in row && Boolean(String(row.left || "").trim() && String(row.right || "").trim()),
    );
    if (!pairs.length) return true;
    return pairs.every((pair) => optionId(given[pair.id] ?? given[String(pair.id)]) != null);
  }
  if (type === "fill_blank") {
    const n = countFillBlanks(String(question?.prompt || ""));
    if (n > 0) {
      const blanks = Array.isArray(src.blanks) ? src.blanks : [];
      return blanks.length >= n && blanks.slice(0, n).every((b) => String(b || "").trim());
    }
    return String(src.answer || "").trim() !== "";
  }
  if (type === "upload") return collectUploadFiles(src).length > 0;
  return String(src.answer || "").trim() !== "";
}

export function responsesAreComplete(
  questions: Array<{ id?: string; type?: string; prompt?: string; options?: unknown }> | null | undefined,
  answers: Record<string, unknown> | null | undefined,
) {
  const map = answers && typeof answers === "object" ? answers : {};
  return (questions || []).every((question) =>
    responseIsComplete(question, map[String(question.id || "")]),
  );
}

export function questionTypeCaption(type: string, presentation?: string | null) {
  if (type === "true_false" || presentation === "true_false") return "True / False";
  if (type === "yes_no" || presentation === "yes_no") return "Yes / No";
  return QUESTION_TYPE_LABELS[type] || type;
}
